import {Link, useNavigate, useLocation} from 'react-router-dom';
import {useState, useRef, useEffect} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHouse, faTerminal, faEnvelope} from '@fortawesome/free-solid-svg-icons';
import {faDocker} from '@fortawesome/free-brands-svg-icons';

function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const navRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);
    const [dragX, setDragX] = useState(null);
    const [startX, setStartX] = useState(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);



    const navItems = [
        {
            path: '/',
            icon: faHouse,
            label: 'House'
        },
        {
            path: '/terminal',
            icon: faTerminal,
            label: 'Terminal'
        },
        {
            path: '/docker',
            icon: faDocker,
            label: 'Docker'
        },
        {
            path: '/mail',
            icon: faEnvelope,
            label: 'Mail'
        }
    ];

    const activeIndex = navItems.findIndex(item => item.path === location.pathname);

    // Обработчики перетаскивания
    const handleStart = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        setStartX(clientX);
        setDragX(clientX);
    };

    const handleMove = (e) => {
        if (startX === null) return;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const distance = Math.abs(clientX - startX);

        // Начинаем драг только если сдвинули больше 5px
        if (distance > 5 && !isDragging) {
            setIsDragging(true);
        }

        if (isDragging) {
            e.preventDefault();

            // Ограничиваем позицию курсора в пределах навбара
            if (navRef.current) {
                const rect = navRef.current.getBoundingClientRect();
                const clampedX = Math.max(rect.left, Math.min(rect.right, clientX));
                setDragX(clampedX);
            } else {
                setDragX(clientX);
            }
        }
    };

    const handleEnd = () => {
        // Если перетаскивали - делаем навигацию
        if (isDragging && dragX !== null && navRef.current) {
            const rect = navRef.current.getBoundingClientRect();
            const relativeX = dragX - rect.left;
            const itemWidth = (rect.width - 8) / navItems.length;

            // Находим ближайший центр элемента
            let closestIndex = Math.round(relativeX / itemWidth - 0.5);
            closestIndex = Math.max(0, Math.min(navItems.length - 1, closestIndex));

            // Навигация через React Router
            if (closestIndex !== activeIndex) {
                navigate(navItems[closestIndex].path);
            }
        }

        setIsDragging(false);
        setDragX(null);
        setStartX(null);
    };

    const getBackgroundStyle = () => {
        if (!navRef.current) {
            return {left: '4px', width: 'auto'};
        }

        const rect = navRef.current.getBoundingClientRect();
        const totalWidth = rect.width;
        const itemWidth = (totalWidth - 8) / navItems.length;

        // Если перетаскиваем - позиционируем по курсору
        if (isDragging && dragX !== null) {
            const relativeX = dragX - rect.left;

            // Вычисляем индекс элемента под курсором (с плавным переходом)
            const floatIndex = (relativeX / itemWidth) - 0.5;
            const clampedIndex = Math.max(0, Math.min(navItems.length - 1, floatIndex));

            // Находим элемент для расчёта ширины
            const targetIndex = Math.round(clampedIndex);
            const targetElement = navRef.current.children[1]?.children[targetIndex];

            if (targetElement) {
                const elementRect = targetElement.getBoundingClientRect();
                const elementWidth = elementRect.width;
                const innerPadding = 4;
                const backgroundHeight = rect.height - 8; // Высота фона (для круга)
                let backgroundWidth = elementWidth + innerPadding * 2;

                // Позиция по центру курсора
                let idealLeft = relativeX - backgroundWidth / 2;
                let idealRight = idealLeft + backgroundWidth;

                // Минимальная ширина = высота (идеальный круг)
                let left = idealLeft;
                let right = idealRight;

                // Проверяем упирание в левую границу
                if (idealLeft < 4) {
                    const overflow = 4 - idealLeft;
                    left = 4;

                    // Сжимаем с правой стороны, но не меньше минимальной ширины
                    const newWidth = Math.max(backgroundHeight, backgroundWidth - overflow);
                    right = left + newWidth;
                    backgroundWidth = newWidth;
                }
                // Проверяем упирание в правую границу
                else if (idealRight > totalWidth - 4) {
                    const overflow = idealRight - (totalWidth - 4);
                    right = totalWidth - 4;

                    // Сжимаем с левой стороны, но не меньше минимальной ширины
                    const newWidth = Math.max(backgroundHeight, backgroundWidth - overflow);
                    left = right - newWidth;
                    backgroundWidth = newWidth;
                }

                return {
                    left: `${left}px`,
                    width: `${backgroundWidth}px`,
                };
            }
        }

        // Находим активный элемент
        const activeElement = navRef.current.children[1]?.children[activeIndex];
        if (activeElement) {
            const elementRect = activeElement.getBoundingClientRect();
            const navRect = navRef.current.getBoundingClientRect();

            const elementLeft = elementRect.left - navRect.left;
            const elementWidth = elementRect.width;
            const elementCenter = elementLeft + elementWidth / 2;

            const innerPadding = 4;
            let backgroundWidth = elementWidth + innerPadding * 2;

            let left = elementCenter - backgroundWidth / 2;
            let right = left + backgroundWidth;

            if (left < 4) {
                const shift = 4 - left;
                left = 4;
                right -= shift;
                backgroundWidth = right - left;
            } else if (right > totalWidth - 4) {
                const shift = right - (totalWidth - 4);
                right = totalWidth - 4;
                left += shift;
                backgroundWidth = right - left;
            }

            return {
                left: `${left}px`,
                width: `${backgroundWidth}px`,
            };
        }

        const position = 4 + activeIndex * itemWidth;
        return {
            left: `${position}px`,
            width: `calc((100% - 8px) / ${navItems.length})`,
        };
    };

    const getMaskClipPath = () => {
        if (activeIndex === -1) {
            return 'inset(0 100% 0 0)';
        }

        if (!navRef.current) {
            return 'inset(0 100% 0 0)';
        }

        const rect = navRef.current.getBoundingClientRect();
        const style = getBackgroundStyle();
        const leftPx = parseFloat(style.left);
        const widthPx = parseFloat(style.width);
        const rightPx = rect.width - leftPx - widthPx;

        return `inset(4px ${rightPx}px 4px ${leftPx}px round 9999px)`;
    };

    return (
        <nav className="fixed bottom-0 left-0 right-0 mb-5 mx-16">
            <div
                className="backdrop-blur-xs rounded-full h-16 border bg-theme-section-bg-color/10 border-theme-section-separator-color/30"
            >
                <div
                    ref={navRef}
                    className="relative flex items-center justify-around h-full gap-1 select-none"
                    onMouseDown={handleStart}
                    onMouseMove={handleMove}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                    onTouchStart={handleStart}
                    onTouchMove={handleMove}
                    onTouchEnd={handleEnd}
                >
                    {/* Анимированный фон */}
                    <div
                        className="absolute rounded-full bg-theme-secondary-bg-color"
                        style={{
                            ...getBackgroundStyle(),
                            height: 'calc(100% - 8px)',
                            top: '4px',
                            transition: isDragging ? 'none' : 'left 0.3s ease-in-out, width 0.3s ease-in-out',
                            willChange: 'left, width',
                        }}
                    />

                    {/* Иконки (базовый слой) */}
                    <div className="absolute inset-0 flex items-center justify-around z-10 gap-1">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className="flex-1 flex flex-col items-center text-theme-text-color justify-center gap-1 rounded-full"
                            >
                                <FontAwesomeIcon
                                    icon={item.icon}
                                    className="text-xl"
                                />
                                <span className="text-[10px] font-medium">{item.label}</span>
                            </Link>
                        ))}
                    </div>

                    {/* Активные иконки (слой с маской) */}
                    <div
                        className="absolute inset-0 flex items-center justify-around z-20 pointer-events-none gap-1"
                        style={{
                            clipPath: getMaskClipPath(),
                            transition: isDragging ? 'none' : 'clip-path 0.3s ease-in-out',
                        }}
                    >
                        {navItems.map((item) => (
                            <div
                                key={`active-${item.path}`}
                                className="flex-1 flex flex-col items-center justify-center gap-1 text-theme-link-color"
                            >
                                <FontAwesomeIcon
                                    icon={item.icon}
                                    className="text-xl font-bold"
                                />
                                <span className="text-[10px] font-semibold">{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
